import React, { useEffect, useRef } from 'react';

const createParticle = (x, y) => ({
  id: Math.random(),
  x: x || Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
  y: y || Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
  size: Math.random() * 3 + 1, // Bigger particles
  speedX: (Math.random() - 0.5) * 0.4,
  speedY: (Math.random() - 0.5) * 0.4,
  opacity: Math.random() * 0.4 + 0.2, // More visible
  life: 1000 + Math.random() * 2000,
  age: 0
});

const ParticleBackground = ({ mousePos = { x: 0, y: 0 } }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const densityRef = useRef(1);
  const frameCountRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Initialize MORE particles
    particlesRef.current = [];
    for (let i = 0; i < 80; i++) { // Increased from 40 to 80
      particlesRef.current.push(createParticle());
    }

    const animate = () => {
      frameCountRef.current++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Check density less frequently and with higher threshold
      if (frameCountRef.current % 120 === 0) { // Every 2 seconds instead of 1
        let connectionCount = 0;
        const maxConnections = particlesRef.current.length * 3; // Higher threshold
        
        particlesRef.current.forEach((particle, index) => {
          particlesRef.current.forEach((otherParticle, otherIndex) => {
            if (index < otherIndex) {
              const dx = particle.x - otherParticle.x;
              const dy = particle.y - otherParticle.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              if (distance < 120) connectionCount++; // Longer connection distance
            }
          });
        });
        
        // More conservative density reduction
        if (connectionCount > maxConnections) {
          densityRef.current = Math.max(0.6, densityRef.current - 0.05); // Less aggressive reduction
        } else if (connectionCount < maxConnections * 0.3) {
          densityRef.current = Math.min(1, densityRef.current + 0.1); // Faster recovery
        }
      }
      
      // Less aggressive particle removal
      if (densityRef.current < 0.9) {
        particlesRef.current = particlesRef.current.filter((particle, index) => {
          particle.age++;
          return !(particle.age > 600 && Math.random() > densityRef.current * 0.8); // Keep more particles
        });
      }
      
      // Maintain more particles
      while (particlesRef.current.length < 60 && densityRef.current > 0.5) { // Higher minimum
        particlesRef.current.push(createParticle());
      }
      
      particlesRef.current.forEach((particle, index) => {
        // Update position
        particle.x += particle.speedX * Math.max(0.7, densityRef.current); // Don't slow down too much
        particle.y += particle.speedY * Math.max(0.7, densityRef.current);
        particle.age++;
        
        // Mouse interaction
        if (mousePos.x && mousePos.y) {
          const dx = mousePos.x - particle.x;
          const dy = mousePos.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            const force = 0.001 * Math.max(0.5, densityRef.current);
            particle.x -= dx * force;
            particle.y -= dy * force;
          }
        }
        
        // Boundary check
        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
        
        // Draw particle with better visibility
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(59, 130, 246, ${particle.opacity * Math.max(0.6, densityRef.current)})`;
        ctx.fill();
        
        // More connections
        particlesRef.current.forEach((otherParticle, otherIndex) => {
          if (index < otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            const connectionDistance = 120 * Math.max(0.7, densityRef.current); // Longer connections
            
            if (distance < connectionDistance) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              
              const alpha = 0.15 * Math.max(0.5, densityRef.current) * (1 - distance / connectionDistance);
              ctx.strokeStyle = `rgba(59, 130, 246, ${alpha})`;
              ctx.lineWidth = 0.8 * Math.max(0.6, densityRef.current);
              ctx.stroke();
            }
          }
        });
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [mousePos]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ background: 'transparent' }}
    />
  );
};

export default ParticleBackground;